import { useState, useEffect, useMemo, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom' 

// Component Imports
import ListWithIcons from '../shared/ListWithIcons';
import Loader from '../shared/Loader';
import ApplicationBar from '../shared/ApplicationBar';

// Context, Hooks, and Types
import { useContextState } from '../../context/Context';
import { useMessagesApi, useShared } from '../../store';
import { Inbox } from '../../models';
import { InboxAction } from '../../context/Reducers'; 
import { InboxStateCategory } from '../../context/InboxContext';

// --- Type Definitions for this component ---

// A flattened shape for the data we will pass to ListWithIcons
interface ListItem {
  id: string;
  title: string;
  subtitle: string;
  originalInbox: Inbox;
}

interface Props {
  // 'private' or 'support' - determines the options shown in the list.
  // The active tab is now controlled by the global 'inboxState'.
  type: string;
  modalType: string;
  setModalType: React.Dispatch<React.SetStateAction<string>>;
  showNewModal: boolean;
  setShowNewModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const InboxList: React.FC<Props> = ({ type, modalType, setModalType, showNewModal, setShowNewModal }) => {
    // --- Hooks and Context ---
    const navigate = useNavigate();
    // const { showModal } = useGlobalModalContext();
    const { inboxCTX } = useContextState();
    const {
        dispatchInboxState,
        inboxState: globalInboxState,
        inboxFilterState: { sort },
    } = inboxCTX;
    const { getInboxes } = useMessagesApi();
    const { dateDifferenceSign } = useShared();
    
    // --- Local State for Pagination and UI ---
    const [pagination, setPagination] = useState({
        // private: { page: 1, hasMore: true },
        // event: { page: 1, hasMore: true },
        supportUser: { page: 1, hasMore: true },
    });
    // const [isLoading, setIsLoading] = useState(false);
    // Use a ref to track the loading state to prevent race conditions in the callback
    const [error, setError] = useState<string | null>(null);

    //  A dedicated state for the initial loading spinner.
    const [isLoading, setIsLoading] = useState(true);
    //  A state to track which categories have completed their first load.
    const [initialLoadComplete, setInitialLoadComplete] = useState({
        supportUser: false,
    });
    
    // --- Derived State for Active Tab ---
    const activeCategory = globalInboxState.inboxState;

    // console.log('inbox: ', globalInboxState)

    const activeInboxList = useMemo(() => {
        switch (activeCategory) {
          // case 'private': return globalInboxState.privateInboxState;
          // case 'event': return globalInboxState.eventsInboxState;
          case 'supportUser': return globalInboxState.supportUserInboxState;
          default: return [];
        }
    }, [activeCategory, globalInboxState]);

    // --- Data Fetching Logic ---
    const fetchInboxes = useCallback(async (category: InboxStateCategory, isInitial = false) => {
      // We will get the current pagination state *inside* the function,
      // but we will update it using a functional update, so we don't need it
      // as a dependency of useCallback.
      
      // Use a local variable for the page to fetch.
      let pageToFetch = 1;

      if (!isInitial) {
          // For subsequent fetches, we get the current page from the state,
          // but we won't put `pagination` in the dependency array.
          setPagination(prev => {
              // If there are no more pages, we shouldn't fetch.
              if (!prev[category].hasMore) return prev;
              pageToFetch = prev[category].page;
              return prev;
          });
      }

      // If we determined we shouldn't fetch, exit early.
      if (!isInitial && !pagination[category].hasMore) return;
      
      setError(null);

      try {
        const apiCall = await getInboxes({ type: "for_support_user", page: pageToFetch });

        if (apiCall?.results) {
          const actionTypeMap = { supportUser: 'SET_SUPPORT_INBOXES' };
          dispatchInboxState({ 
            type: isInitial ? 'REPLACE_INBOX_LIST' : actionTypeMap[category], 
            payload: { inboxes: apiCall.results, category } 
          } as InboxAction);
          
          // Use the functional update form here.
          // This allows us to update the state without needing `pagination` in the dependency array.
          setPagination(prev => ({ 
            ...prev, 
            [category]: { page: pageToFetch + 1, hasMore: Boolean(apiCall.next) } 
          }));
        } else {
          if (isInitial) {
              dispatchInboxState({ type: 'REPLACE_INBOX_LIST', payload: { inboxes: [], category } } as InboxAction);
          }
          setPagination(prev => ({ ...prev, [category]: { ...prev[category], hasMore: false } }));
        }
      } catch (err) {
        console.error(`Failed to fetch inboxes for ${category}:`, err);
        setError("Failed to load inboxes. Please try again.");
      } finally {
        if (isInitial) {
            setInitialLoadComplete(prev => ({ ...prev, [category]: true }));
            setIsLoading(false);
        }
      }
    }, [getInboxes, dispatchInboxState]);

    // --- Effect to fetch data when the active tab changes ---
    useEffect(() => {
        // If the initial load for the active category has NOT been completed yet,
        // show the loader and fetch the data.
        if (!initialLoadComplete[activeCategory]) {
            setIsLoading(true);
            fetchInboxes(activeCategory, true);
        } else {
            // If the data has already been loaded, just make sure the loader is off.
            setIsLoading(false);
        }
    }, [activeCategory, initialLoadComplete, fetchInboxes]);

    // --- Memoized Data Transformation and Options ---
    const processedListItems = useMemo((): ListItem[] => {
        let list = activeInboxList || [];
        if (sort) {
        list = [...list].sort((a, b) =>
            sort === "soonToLater"
            ? dateDifferenceSign(a.last_messages_time, b.last_messages_time)
            : dateDifferenceSign(b.last_messages_time, a.last_messages_time)
        );
        }

        return list.map(inbox => {
            const hasUnread = typeof inbox.count === 'number' && inbox.count > 0;
            return {
                id: inbox.id,
                title: inbox.subject,
                subtitle: hasUnread ? `${inbox.count} unread` : '0 Unread',
                icon: inbox.icon,
                originalInbox: inbox,
            }});
    }, [activeInboxList, sort, dateDifferenceSign]);

    // --- Render Logic ---
    const renderContent = () => {
        if (isLoading) {
          return <div className='mt-12'><Loader /></div>;
        }

        if (error) {
            return <div className='p-6 text-center'><h6 className='mt-12 text-red-500'>{error}</h6></div>;
        }
      return (
        <InfiniteScroll
          dataLength={processedListItems.length}
          next={() => fetchInboxes(activeCategory)}
          hasMore={pagination[activeCategory].hasMore}
          loader={<Loader />}
          endMessage={<p className='my-6 text-center text-gray-500'>You've reached the end.</p>}
        >
          <ListWithIcons
            listItems={processedListItems}
            titleKey="title"
            subtitleKey="subtitle"
            onItemClick={(item) => navigate(`/messages?id=${item.id}`)}
            itemOptions={[]}
          />
        </InfiniteScroll>
      );
    };

  return (
    <div className="bg-white mt-12 pb-24 mx-auto max-w-3xl px-6 lg:px-8">
      <p className="flex justify-center mt-1 max-w-2xl text-md leading-6 font-bold">Support Messages</p>
      {renderContent()}
      
      <ApplicationBar />
    </div>
  );
};

export default InboxList;